/**
 * Web half of the SERVER-enforced login lockout (CAMP-2026-0949).
 *
 * The lock used to live only in the browser: five failures set a component
 * state, and a refresh threw it away — the ticket's "refresh, type the right
 * password, you are in". The API now owns the lock (apps/api/src/lib/
 * login-lockout.ts): five failed attempts on one identifier lock that identifier
 * for 300 s, and a locked identifier is refused even with the correct password.
 * The sign-in screens therefore stopped counting anything. All they do is
 * obey the verdict:
 *
 *   429  { success:false, error:{ code:"ACCOUNT_LOCKED", message, retryAfterSeconds } }
 *        + `Retry-After: <s>`
 *
 * This module turns that verdict into a lock the screen can show, and keeps it
 * in `sessionStorage` per NORMALISED identifier so a refresh (or a remount)
 * brings the timer straight back once the same identifier is typed again. The
 * storage is a convenience, not the enforcement: clear it and the API still
 * says 429.
 *
 * Deliberately free of `@/api/client` — `lib/kiosk-session.ts` imports this file
 * and client.ts imports kiosk-session, so reaching back into the client here
 * would close an import cycle.
 */
import axios from "axios";
import { z } from "zod";
import { isPhoneNumber, normalizePhone } from "@/lib/login-identifier";

/** sessionStorage key: a JSON array of `{ identifier, lockedUntil }`. */
export const LOGIN_LOCK_STORAGE_KEY = "campushubai-login-lock";

/** What the API locks for. Only used when a lock verdict arrives with no number at all. */
export const LOGIN_LOCK_FALLBACK_SECONDS = 300;

/** A hostile or buggy `Retry-After` must not park the form for a day. */
export const LOGIN_LOCK_MAX_SECONDS = 3600;

/** More distinct identifiers than this in one tab is not a person signing in. */
const MAX_STORED_LOCKS = 20;

// ── identifier ──────────────────────────────────────────────────────────────

/**
 * The lock key for whatever is typed in the username box — the same shape the
 * API derives (`email:<trim+lower>`, else `phone:<10 digits>`), so "Ravi@X.com "
 * and "ravi@x.com" are one identifier, and `+91 98765 43210` is `9876543210`.
 * Null for an empty box. No IP and no password: the lock follows the account.
 */
export function loginLockIdentifier(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (isPhoneNumber(trimmed)) {
    const phone = normalizePhone(trimmed);
    return phone ? `phone:${phone}` : null;
  }
  return `email:${trimmed.toLowerCase()}`;
}

// ── the server's verdict ────────────────────────────────────────────────────

export type LoginLockCode = "ACCOUNT_LOCKED" | "RATE_LIMITED";

export interface LoginLockVerdict {
  code: LoginLockCode;
  /** Whole seconds, 1..LOGIN_LOCK_MAX_SECONDS. */
  retryAfterSeconds: number;
  /** The API's own sentence, when it sent one. */
  message: string | null;
}

// Tolerant on purpose: a malformed `message` / `retryAfterSeconds` must not hide
// the `code`, because the code is what says "this is a lock". Unknown extra
// fields are fine (the API adds `retryAfterSeconds` additively).
const verdictBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string().optional().catch(undefined),
    retryAfterSeconds: z.number().finite().positive().optional().catch(undefined),
  }),
});

function clampSeconds(seconds: number): number {
  return Math.min(LOGIN_LOCK_MAX_SECONDS, Math.max(1, Math.ceil(seconds)));
}

/** The `Retry-After` value from axios headers (an AxiosHeaders, or a plain object in tests). */
function retryAfterHeader(headers: unknown): string | null {
  if (!headers || typeof headers !== "object") return null;
  const getter = (headers as { get?: unknown }).get;
  if (typeof getter === "function") {
    const viaGet: unknown = (getter as (name: string) => unknown).call(headers, "retry-after");
    if (typeof viaGet === "string" || typeof viaGet === "number") return String(viaGet);
  }
  const record = headers as Record<string, unknown>;
  const direct = record["retry-after"] ?? record["Retry-After"];
  return typeof direct === "string" || typeof direct === "number" ? String(direct) : null;
}

/** `Retry-After` is delta-seconds or an HTTP date (RFC 9110 §10.2.3). */
function secondsFromRetryAfter(value: string | null, now: number): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    return n > 0 ? n : null;
  }
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  const delta = (at - now) / 1000;
  return delta > 0 ? delta : null;
}

/**
 * Is this failed request the API telling us to wait?
 *
 * Reads a 429 whose body code is ACCOUNT_LOCKED or RATE_LIMITED and finds the
 * wait in `retryAfterSeconds`, else the `Retry-After` header. An ACCOUNT_LOCKED
 * with neither falls back to the lock's own length (300 s). A RATE_LIMITED with
 * neither is the per-network ceiling ("Too many attempts from this network.
 * Try again shortly.") — it names no wait, so it is NOT a timer; the caller
 * shows its message as an ordinary error. Anything else (401, 5xx, network,
 * 429 with some other code) is null.
 */
export function readLoginLockVerdict(err: unknown, now: number = Date.now()): LoginLockVerdict | null {
  const response = axios.isAxiosError(err) ? err.response : undefined;
  if (!response || response.status !== 429) return null;
  const parsed = verdictBodySchema.safeParse(response.data);
  if (!parsed.success) return null;
  const { code, message, retryAfterSeconds } = parsed.data.error;
  if (code !== "ACCOUNT_LOCKED" && code !== "RATE_LIMITED") return null;

  const seconds =
    retryAfterSeconds ??
    secondsFromRetryAfter(retryAfterHeader(response.headers), now) ??
    (code === "ACCOUNT_LOCKED" ? LOGIN_LOCK_FALLBACK_SECONDS : null);
  if (seconds === null) return null;

  const trimmedMessage = message?.trim();
  return { code, retryAfterSeconds: clampSeconds(seconds), message: trimmedMessage ? trimmedMessage : null };
}

/**
 * The sentence to print for a lock verdict where the screen has no i18n of its
 * own (Control Center, the kiosk gate): the API's message, else a plain one.
 */
export function loginLockMessage(verdict: LoginLockVerdict): string {
  return (
    verdict.message ??
    `Too many failed sign-in attempts. Try again in ${verdict.retryAfterSeconds} seconds or use a one-time code.`
  );
}

// ── persistence (sessionStorage, Zod-validated on read) ─────────────────────

const storedLocksSchema = z.array(
  z.object({
    identifier: z.string().min(1).max(400),
    lockedUntil: z.number().finite().positive(),
  }),
);
type StoredLock = z.infer<typeof storedLocksSchema>[number];

/** Only used when sessionStorage refuses a write (some private modes): the tab still obeys the lock. */
const memoryLocks = new Map<string, number>();

function session(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Every lock still running at `now`. Junk in storage reads as "no locks" and is removed. */
function readAll(now: number): StoredLock[] {
  const merged = new Map<string, number>();
  const storage = session();
  if (storage) {
    try {
      const raw = storage.getItem(LOGIN_LOCK_STORAGE_KEY);
      if (raw !== null) {
        const parsed = storedLocksSchema.safeParse(JSON.parse(raw));
        if (parsed.success) {
          for (const entry of parsed.data) {
            if (entry.lockedUntil > now) merged.set(entry.identifier, Math.max(entry.lockedUntil, merged.get(entry.identifier) ?? 0));
          }
        } else {
          storage.removeItem(LOGIN_LOCK_STORAGE_KEY);
        }
      }
    } catch {
      try { storage.removeItem(LOGIN_LOCK_STORAGE_KEY); } catch { /* nothing more to do */ }
    }
  }
  for (const [identifier, lockedUntil] of memoryLocks) {
    if (lockedUntil > now) merged.set(identifier, Math.max(lockedUntil, merged.get(identifier) ?? 0));
    else memoryLocks.delete(identifier);
  }
  return [...merged].map(([identifier, lockedUntil]) => ({ identifier, lockedUntil }));
}

function writeAll(locks: StoredLock[]): void {
  const newest = [...locks].sort((a, b) => b.lockedUntil - a.lockedUntil).slice(0, MAX_STORED_LOCKS);
  const storage = session();
  try {
    if (!storage) throw new Error("no sessionStorage");
    if (newest.length === 0) storage.removeItem(LOGIN_LOCK_STORAGE_KEY);
    else storage.setItem(LOGIN_LOCK_STORAGE_KEY, JSON.stringify(newest));
    memoryLocks.clear();
  } catch {
    memoryLocks.clear();
    for (const { identifier, lockedUntil } of newest) memoryLocks.set(identifier, lockedUntil);
  }
}

/** When the lock on `identifier` ends (epoch ms), or null if it is not locked. Expired locks are pruned. */
export function readLoginLock(identifier: string, now: number = Date.now()): number | null {
  return readAll(now).find((l) => l.identifier === identifier)?.lockedUntil ?? null;
}

/** Remember that `identifier` is locked until `lockedUntil` (epoch ms). */
export function saveLoginLock(identifier: string, lockedUntil: number, now: number = Date.now()): void {
  if (!identifier || !Number.isFinite(lockedUntil) || lockedUntil <= now) return;
  const others = readAll(now).filter((l) => l.identifier !== identifier);
  writeAll([...others, { identifier, lockedUntil: Math.round(lockedUntil) }]);
}

/** Forget the lock on `identifier` — it expired, or the sign-in succeeded. */
export function clearLoginLock(identifier: string): void {
  memoryLocks.delete(identifier);
  const remaining = readAll(Date.now()).filter((l) => l.identifier !== identifier);
  writeAll(remaining);
}

/**
 * If `err` is a lock verdict, remember it against `identifier` and return it.
 * The one call a failed sign-in makes; null means "an ordinary failure".
 */
export function recordLoginLockVerdict(
  identifier: string | null,
  err: unknown,
  now: number = Date.now(),
): LoginLockVerdict | null {
  const verdict = readLoginLockVerdict(err, now);
  if (verdict && identifier) saveLoginLock(identifier, now + verdict.retryAfterSeconds * 1000, now);
  return verdict;
}

/** Whole seconds left on a lock, never negative. */
export function loginLockSecondsLeft(lockedUntil: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((lockedUntil - now) / 1000));
}
